import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { MessageCircle, Save, Edit3 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MessagingConfiguration = () => {
  const [whatsappConfig, setWhatsappConfig] = useState({ default_message: '', is_enabled: true });
  const [viberConfig, setViberConfig] = useState({ default_message: '', is_enabled: true });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingWhatsApp, setEditingWhatsApp] = useState(false);
  const [editingViber, setEditingViber] = useState(false);

  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    try {
      const [whatsappResponse, viberResponse] = await Promise.all([
        axios.get(`${API}/messaging/whatsapp/config`),
        axios.get(`${API}/messaging/viber/config`)
      ]);
      
      setWhatsappConfig(whatsappResponse.data);
      setViberConfig(viberResponse.data);
    } catch (error) {
      console.error("Fetch messaging configs error:", error);
      toast.error("Gabim në ngarkimin e konfigurimit");
    } finally {
      setLoading(false);
    }
  };

  const updateWhatsAppConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/messaging/whatsapp/config?default_message=${encodeURIComponent(whatsappConfig.default_message)}&is_enabled=${whatsappConfig.is_enabled}`);
      toast.success("Konfigurimi i WhatsApp u përditësua");
      setEditingWhatsApp(false);
    } catch (error) {
      toast.error("Gabim në përditësimin e WhatsApp");
      console.error("WhatsApp config update error:", error);
    } finally {
      setSaving(false);
    }
  };

  const updateViberConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/messaging/viber/config?default_message=${encodeURIComponent(viberConfig.default_message)}&is_enabled=${viberConfig.is_enabled}`);
      toast.success("Konfigurimi i Viber u përditësua");
      setEditingViber(false);
    } catch (error) {
      toast.error("Gabim në përditësimin e Viber");
      console.error("Viber config update error:", error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* WhatsApp Configuration */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="flex items-center text-green-700">
                <MessageCircle className="w-5 h-5 mr-2" />
                WhatsApp Konfigurimi
              </CardTitle>
              <CardDescription>
                Konfiguro mesazhin default për WhatsApp integrim
              </CardDescription>
            </div>
            <div className="flex items-center space-x-2">
              <Badge variant={whatsappConfig.is_enabled ? "default" : "secondary"}>
                {whatsappConfig.is_enabled ? "Aktiv" : "Joaktiv"}
              </Badge>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setEditingWhatsApp(!editingWhatsApp)}
              >
                <Edit3 className="w-4 h-4 mr-1" />
                {editingWhatsApp ? "Anulo" : "Edito"}
              </Button>
            </div>
          </div>
        </CardHeader>
        
        <CardContent>
          {editingWhatsApp ? (
            <div className="space-y-4">
              <div>
                <Label htmlFor="whatsapp_message">Mesazhi Default</Label>
                <Textarea
                  id="whatsapp_message"
                  value={whatsappConfig.default_message}
                  onChange={(e) => setWhatsappConfig(prev => ({ ...prev, default_message: e.target.value }))}
                  placeholder="Shkruaj mesazhin default për WhatsApp..."
                  rows={4}
                />
                <p className="text-xs text-slate-500 mt-1">
                  Mund të përdorni: {`{client_name}`}, {`{ticket_number}`} si placeholders
                </p>
              </div>
              
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="whatsapp_enabled"
                  checked={whatsappConfig.is_enabled}
                  onChange={(e) => setWhatsappConfig(prev => ({ ...prev, is_enabled: e.target.checked }))}
                />
                <Label htmlFor="whatsapp_enabled">Aktivizo WhatsApp integrim</Label>
              </div>
              
              <div className="flex space-x-2">
                <Button onClick={updateWhatsAppConfig} disabled={saving}>
                  <Save className="w-4 h-4 mr-2" />
                  Ruaj Ndryshimet
                </Button>
                <Button variant="outline" onClick={() => setEditingWhatsApp(false)}>
                  Anulo
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <h4 className="text-sm font-medium text-slate-700">Mesazhi Aktual:</h4>
                <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded border">
                  {whatsappConfig.default_message || "Nuk ka mesazh të konfiguruar"}
                </p>
              </div>
              
              <div className="text-xs text-slate-500">
                <strong>Shembull përdorimi:</strong> Kur klikoni WhatsApp pranë numrit të telefonit, ky mesazh do të dërgohet automatikisht.
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Viber Configuration */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="flex items-center text-purple-700">
                <MessageCircle className="w-5 h-5 mr-2" />
                Viber Konfigurimi
              </CardTitle>
              <CardDescription>
                Konfiguro mesazhin default për Viber integrim
              </CardDescription>
            </div>
            <div className="flex items-center space-x-2">
              <Badge variant={viberConfig.is_enabled ? "default" : "secondary"}>
                {viberConfig.is_enabled ? "Aktiv" : "Joaktiv"}
              </Badge>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setEditingViber(!editingViber)}
              >
                <Edit3 className="w-4 h-4 mr-1" />
                {editingViber ? "Anulo" : "Edito"}
              </Button>
            </div>
          </div>
        </CardHeader>
        
        <CardContent>
          {editingViber ? (
            <div className="space-y-4">
              <div>
                <Label htmlFor="viber_message">Mesazhi Default</Label>
                <Textarea
                  id="viber_message"
                  value={viberConfig.default_message}
                  onChange={(e) => setViberConfig(prev => ({ ...prev, default_message: e.target.value }))}
                  placeholder="Shkruaj mesazhin default për Viber..."
                  rows={4}
                />
                <p className="text-xs text-slate-500 mt-1">
                  Mund të përdorni: {`{client_name}`}, {`{ticket_number}`} si placeholders
                </p>
              </div>
              
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="viber_enabled"
                  checked={viberConfig.is_enabled}
                  onChange={(e) => setViberConfig(prev => ({ ...prev, is_enabled: e.target.checked }))}
                />
                <Label htmlFor="viber_enabled">Aktivizo Viber integrim</Label>
              </div>
              
              <div className="flex space-x-2">
                <Button onClick={updateViberConfig} disabled={saving}>
                  <Save className="w-4 h-4 mr-2" />
                  Ruaj Ndryshimet
                </Button>
                <Button variant="outline" onClick={() => setEditingViber(false)}>
                  Anulo
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <h4 className="text-sm font-medium text-slate-700">Mesazhi Aktual:</h4>
                <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded border">
                  {viberConfig.default_message || "Nuk ka mesazh të konfiguruar"}
                </p>
              </div>
              
              <div className="text-xs text-slate-500">
                <strong>Shembull përdorimi:</strong> Kur klikoni Viber pranë numrit të telefonit, ky mesazh do të dërgohet automatikisht.
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Usage Instructions */}
      <Card>
        <CardHeader>
          <CardTitle>Udhëzime Përdorimi</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 text-sm text-slate-600">
            <div>
              <strong>1. Placeholders që mund të përdorni:</strong>
              <ul className="list-disc list-inside ml-4 mt-1">
                <li><code>{`{client_name}`}</code> - Emri i klientit</li>
                <li><code>{`{ticket_number}`}</code> - Numri i tiketit</li>
              </ul>
            </div>
            
            <div>
              <strong>2. Ku shfaqen butonat:</strong>
              <ul className="list-disc list-inside ml-4 mt-1">
                <li>Dashboard - pranë numrave të telefonit në tiketa</li>
                <li>Faqja e klientëve - pranë informacioneve të kontaktit</li>
                <li>Detajet e tiketave - pranë numrit të telefonit të klientit</li>
              </ul>
            </div>
            
            <div>
              <strong>3. Si funksionon:</strong>
              <ul className="list-disc list-inside ml-4 mt-1">
                <li>WhatsApp: Hap WhatsApp Web/App me mesazh të paracaktuar</li>
                <li>Viber: Përpiqet të hapë Viber App, përndryshe udhëzon për Viber Web</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MessagingConfiguration;